import { enforce, omitWhen, test } from 'vest';

import {
  isEndpointUrl,
  withDefaults,
} from 'admin-panel/utils/integrations/config';

import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';
import type {
  IntegrationFormValues,
  ServiceFormDefinition,
} from 'admin-panel/utils/integrations/types';

export interface OhipService {
  authenticationMethod: 'ocim' | 'ssd';
  apiBaseUrl: string;
  appKey: string;
  clientId: string;
  clientSecret: string;
  chainCode: string;
  hotelCode: string;
  region: string;
  enterpriseId: string;
  authScope: string;
  username: string;
  password: string;
}

export const OHIP_AUTHENTICATION_METHODS = ['ocim', 'ssd'];

export const OHIP_REGIONS = [
  '',
  'Ashburn',
  'Frankfurt',
  'Hyderabad',
  'Melbourne',
  'Mumbai',
  'Other',
  'Singapore',
  'Sydney',
];

const SERVICE = 'currentConfig.service';

const defaultConfig = (): IntegrationConfig => ({
  service: {
    authenticationMethod: 'ocim',
    apiBaseUrl: '',
    appKey: '',
    clientId: '',
    clientSecret: '',
    chainCode: '',
    hotelCode: '',
    region: '',
    enterpriseId: '',
    authScope: '',
    username: '',
    password: '',
  },
  features: {},
  cron: [],
  webhooks: [],
});

export const ohip: ServiceFormDefinition = {
  toFormConfig: (config) => withDefaults(defaultConfig(), config),

  validate(values: IntegrationFormValues, t) {
    const service = values.currentConfig.service as unknown as OhipService;

    test(`${SERVICE}.authenticationMethod`, t('validations.invalid'), () => {
      enforce(service.authenticationMethod).inside(OHIP_AUTHENTICATION_METHODS);
    });

    test(`${SERVICE}.apiBaseUrl`, t('validations.required'), () => {
      enforce(service.apiBaseUrl).isNotBlank();
    });

    omitWhen(!service.apiBaseUrl, () => {
      test(`${SERVICE}.apiBaseUrl`, t('validations.url'), () => {
        enforce(isEndpointUrl(service.apiBaseUrl)).isTruthy();
      });
    });

    for (const field of [
      'appKey',
      'clientId',
      'clientSecret',
      'chainCode',
      'hotelCode',
    ] as const) {
      test(`${SERVICE}.${field}`, t('validations.required'), () => {
        enforce(service[field]).isNotBlank();
      });
    }

    test(`${SERVICE}.region`, t('validations.invalid'), () => {
      enforce(service.region).inside(OHIP_REGIONS);
    });

    omitWhen(service.authenticationMethod !== 'ocim', () => {
      for (const field of ['enterpriseId', 'authScope'] as const) {
        test(`${SERVICE}.${field}`, t('validations.required'), () => {
          enforce(service[field]).isNotBlank();
        });
      }
    });

    omitWhen(service.authenticationMethod !== 'ssd', () => {
      for (const field of ['username', 'password'] as const) {
        test(`${SERVICE}.${field}`, t('validations.required'), () => {
          enforce(service[field]).isNotBlank();
        });
      }
    });
  },
};
